/**
 * What the Dashboard should put in front of someone, in order.
 *
 * **The Dashboard is the workplace, not a summary of the other tabs** (owner decision, 2026-08-15):
 * it exists so the next important thing can be *done here*, and so someone can sort out what is
 * wrong without navigating anywhere. That makes the ordering the product — a screen that lists
 * everything equally is a scoreboard, and a scoreboard sends you to another tab to act.
 *
 * Pure on purpose. The ordering is the part worth testing, and it is the part that would otherwise
 * only be verifiable by staging four different account states in a browser.
 *
 * ⚠️ **`null` is not zero, here more than anywhere.** A figure we could not load must never sort as
 * "nothing to do" — that is rule 5 (not-knowable is not a value) pointed at a work queue, where the
 * consequence is silently hiding the one thing that needed attention. An unknown count yields an
 * item whose control is `unknown`, so the screen says it could not look rather than saying it is
 * clear.
 */

/** The state the queue is derived from. Every count is nullable: unknown ≠ none. */
export interface WorkState {
  /** Connections that exist, and whether each is asking to be reconnected. */
  connections: { provider: string; needsReconnect: boolean }[] | null;
  /** Sources whose sync is failing or degraded — hosted tier only; null when unknown. */
  unhealthySources: number | null;
  /** Records staged/loaded. 0 with a connection present means "connected but never pulled". */
  records: number | null;
  /** Undecided candidate pairs. */
  undecidedPairs: number | null;
  /** Actions proposed and waiting on a human. */
  proposedActions: number | null;
  /** Open findings. */
  openFindings: number | null;
}

/**
 * `control` names the thing the Dashboard renders INLINE for this item. It is not a route: a route
 * would be the navigation this screen exists to remove.
 */
export type WorkControl =
  | 'reconnect'
  | 'connect'
  | 'sync'
  | 'approve'
  | 'decide'
  | 'findings'
  | 'unknown';

export interface WorkItem {
  id: string;
  control: WorkControl;
  /** What is true, in the reader's terms. */
  title: string;
  /** Why it is worth doing now — never a restatement of the title. */
  why: string;
  /** The figure behind it, where there is one. `null` means we could not read it. */
  count: number | null;
}

/**
 * Ordered most-urgent first. The order encodes a judgement, so it is written down:
 *
 *  1. **A connection asking to be reconnected** outranks everything, because it is the only state
 *     that silently makes every other number on the screen stale. A queue that looks calm because
 *     its source stopped reporting is the worst failure this screen can have.
 *  2. **Nothing connected** — there is no product until there is.
 *  3. **Connected but nothing pulled** — the setup step people abandon, because nothing prompts it.
 *  4. **Actions proposed** before pairs to decide: an action is a change staged against the
 *     customer's own system and is the closest thing here to an irreversible act, so it gets a
 *     human sooner. A pair waiting is inert by comparison.
 *  5. **Pairs to decide** — the core work.
 *  6. **Open findings** — ranked advice, genuinely lower priority than a decision already queued.
 *
 * An unreadable figure is surfaced rather than skipped, at the position its subject would occupy.
 */
export function deriveWork(state: WorkState): WorkItem[] {
  const items: WorkItem[] = [];
  const conns = state.connections;

  const reconnecting = conns?.filter((c) => c.needsReconnect) ?? [];
  if (reconnecting.length > 0) {
    items.push({
      id: 'reconnect',
      control: 'reconnect',
      count: reconnecting.length,
      title:
        reconnecting.length === 1
          ? `${reconnecting[0].provider} needs reconnecting`
          : `${reconnecting.length} connections need reconnecting`,
      why: 'Until it is reconnected nothing new arrives, and every count below describes the data as it was — not as it is.',
    });
  }

  if (state.unhealthySources !== null && state.unhealthySources > 0) {
    items.push({
      id: 'unhealthy',
      control: 'reconnect',
      count: state.unhealthySources,
      title: `${state.unhealthySources} source${state.unhealthySources === 1 ? '' : 's'} not syncing cleanly`,
      why: 'Findings from a degraded source may be stale, so what you decide here could be based on old records.',
    });
  }

  /* Only when we actually know there are none. `null` is a separate item below — the difference
     between "you have connected nothing" and "we could not ask" is the whole point. */
  if (conns !== null && conns.length === 0) {
    items.push({
      id: 'connect',
      control: 'connect',
      count: 0,
      title: 'Connect a system, or load a file',
      why: 'Dedupr reads only what you point it at. A connection is read-only — no write access is requested until you approve a change that needs it.',
    });
  } else if (conns === null) {
    items.push({
      id: 'connections-unknown',
      control: 'unknown',
      count: null,
      title: 'We could not check your connections',
      why: 'This is not the same as having none. Nothing below is reliable until this reads.',
    });
  } else if (state.records === 0) {
    items.push({
      id: 'sync',
      control: 'sync',
      count: 0,
      title: 'Pull your records',
      why: 'The connection is approved but nothing has been read yet, so there is nothing to find duplicates in.',
    });
  }

  if (state.proposedActions !== null && state.proposedActions > 0) {
    items.push({
      id: 'approve',
      control: 'approve',
      count: state.proposedActions,
      title: `${state.proposedActions} change${state.proposedActions === 1 ? '' : 's'} waiting for your approval`,
      why: 'Nothing is applied until you approve it. Each one is snapshotted first and can be undone for 30 days.',
    });
  }

  if (state.undecidedPairs !== null && state.undecidedPairs > 0) {
    items.push({
      id: 'decide',
      control: 'decide',
      count: state.undecidedPairs,
      title: `${state.undecidedPairs} duplicate${state.undecidedPairs === 1 ? '' : 's'} to decide`,
      why: 'Each one shows the evidence behind it. Nothing merges itself.',
    });
  }

  if (state.openFindings !== null && state.openFindings > 0) {
    items.push({
      id: 'findings',
      control: 'findings',
      count: state.openFindings,
      title: `${state.openFindings} open finding${state.openFindings === 1 ? '' : 's'}`,
      why: 'Ranked suggestions about your data — worth a look once the queue above is clear.',
    });
  }

  return items;
}

/**
 * True when there is genuinely nothing outstanding — as opposed to nothing *readable*, which
 * `deriveWork` reports as an item. Used to choose between "you are clear" and a work queue; the two
 * must never be reachable from the same state.
 */
export function isAllClear(items: WorkItem[]): boolean {
  return items.length === 0;
}
